import _ from "lodash";
import React, { useMemo } from "react";
import { connect } from "react-redux";

import ChatContainer from "AppComponents/ChatContainer";
import { NoMessagesIllustration } from "@toddle-edu/ds-theme";

import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getConversationReadRefetchQueries } from "ClassRoom/modules/ClassRoomModule";

import {
  getIsArchivedClass,
  getIsCurrentAcademicYearSelected,
  getIsParent,
  getChildId,
  getUserType,
} from "modules/CommonSelectors";
import { useI18n } from "Hooks";

import classes from "./StudentAssignmentMessaging.scss";

const styles = {
  footerContainerStyle: {
    minHeight: "64px",
    justifyContent: "center",
  },
  messagesScrollStyle: {
    paddingTop: "8px",
  },
};

const StudentAssignmentMessaging = React.memo(props => {
  const {
    studentAssignmentId,
    activeMessagingPane,
    isRemovedCourseForLoggedInUser,
    conversationId,
    assignmentTitle,
    assessmentId,
    classDiscussion,
    isClassDiscussionEnabled,
    isStudentEvaluationEnabled,
    isMediaRestricted = false,
    userInfo,
    userType,
    isParent,
    isArchivedClass,
    isCurrentAcademicYear,
    childID,
    getConversationReadRefetchQueries,
    shouldRefetchConversationOnEvent = () => false,
  } = props;

  const { t } = useI18n();

  const classDiscussionConversationId = _.get(classDiscussion, "id", "");

  const studentData = useMemo(
    () =>
      userType === "parent"
        ? {
            firstName: _.get(userInfo, "childFirstName"),
            middleName: _.get(userInfo, "childMiddleName"),
            lastName: _.get(userInfo, "childLastName"),
          }
        : {},
    [userType, userInfo]
  );

  switch (activeMessagingPane) {
    case "ASSIGNMENT_MESSAGE": // private message
      return (
        <div className={classes.chatContainer}>
          <ChatContainer
            isV2={true}
            allowSendMessage={
              !isParent &&
              !isRemovedCourseForLoggedInUser &&
              !isArchivedClass &&
              isCurrentAcademicYear
            }
            isMediaRestricted={isMediaRestricted}
            parentType={"STUDENT_ASSIGNMENT"}
            showUserDetails={false}
            showSenderInfoAlways={true}
            parentId={studentAssignmentId}
            conversationId={conversationId}
            conversationLabel={assignmentTitle}
            showMessageCount={false}
            showInstructionMessage={false}
            otherUserId={childID}
            showAllMessages={false}
            inputTextPlaceHolder={`${t("common:typeHere")}...`}
            excludeItemTypesV2={["STUDENT_ASSIGNMENT_SUBMISSION_FEEDBACK"]}
            excludedItemTypesForConversation={
              "STUDENT_ASSIGNMENT_SUBMISSION_FEEDBACK"
            }
            mode={
              isRemovedCourseForLoggedInUser ||
              isArchivedClass ||
              !isCurrentAcademicYear ||
              isParent
                ? "view"
                : "edit"
            }
            t={t}
            isCurrentAcademicYear={isCurrentAcademicYear}
            emptyText={"classRoom:send_private_message_to_teacher"}
            emptyImageUrl={NoMessagesIllustration}
            footerContainerStyle={styles.footerContainerStyle}
            testIdPrefix={"studentAssignment-privateMessages"}
            refetchQueries={getConversationReadRefetchQueries()}
            messagesScrollStyle={styles.messagesScrollStyle}
            studentData={studentData}
            shouldRefetchConversationOnEvent={shouldRefetchConversationOnEvent}
          />
        </div>
      );

    case "ASSIGNMENT_GROUP_MESSAGE": // class discussion
      return (
        <div className={classes.chatContainer}>
          <ChatContainer
            isV2={true}
            deleteOtherMessage={false}
            parentType={"ASSIGNMENT"}
            showSenderInfoAlways={true}
            showUserDetails={false}
            parentId={assessmentId}
            conversationId={classDiscussionConversationId}
            conversationLabel={assignmentTitle}
            inputTextPlaceHolder={`${t("common:typeHere")}...`}
            isStudentEvaluationEnabled={isStudentEvaluationEnabled}
            allowSendMessage={
              isClassDiscussionEnabled &&
              !isParent &&
              !isRemovedCourseForLoggedInUser &&
              !isArchivedClass &&
              isCurrentAcademicYear
            }
            otherUserId={childID}
            messagesConStyle={isParent ? styles.messagesConStyleTwo : null}
            messagesScrollStyle={styles.messagesScrollStyle}
            isClassDiscussionEnabled={isClassDiscussionEnabled}
            excludeItemTypesV2={"STUDENT_ASSIGNMENT_SUBMISSION_FEEDBACK"}
            excludedItemTypesForConversation={
              "STUDENT_ASSIGNMENT_SUBMISSION_FEEDBACK"
            }
            mode={
              isRemovedCourseForLoggedInUser ||
              isArchivedClass ||
              !isCurrentAcademicYear ||
              isParent
                ? "view"
                : "edit"
            } // disabling message editing dropdown in view mode
            t={t}
            showNoDataComp={isParent && isClassDiscussionEnabled}
            isCurrentAcademicYear={isCurrentAcademicYear}
            emptyHeaderText={
              isClassDiscussionEnabled
                ? "classRoom:no_discussion_yet"
                : "classRoom:class_discussion_disabled"
            }
            emptyText={
              isClassDiscussionEnabled
                ? "classRoom:send_message_to_initiate_a_dicussion_msg"
                : "classRoom:teacher_disabled_classdiscussion"
            }
            showEmptyText={isClassDiscussionEnabled}
            footerContainerStyle={styles.footerContainerStyle}
            testIdPrefix={"studentAssignment-classDiscussion"}
            refetchQueries={getConversationReadRefetchQueries()}
            studentData={studentData}
            excludedMessageTypes={["SYSTEM_GENERATED_GROUP_CHAT_SECOND"]}
          />
        </div>
      );

    default:
      return null;
  }
});

StudentAssignmentMessaging.displayName = "StudentAssignmentMessaging";

const mapActionCreators = {
  getConversationReadRefetchQueries,
};

const mapStateToProps = state => {
  return {
    isParent: getIsParent(state),
    isArchivedClass: getIsArchivedClass(state),
    isCurrentAcademicYear: getIsCurrentAcademicYearSelected(state),
    childID: getChildId(state),
    userType: getUserType(state),
    userInfo: getUserInfo(),
  };
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(StudentAssignmentMessaging);
